import { useState, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { 
  Tag, 
  Plus, 
  History,
  AlertCircle,
  CheckCircle,
  Loader2,
  Clock,
  Users,
  XCircle,
  Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPromoCodesPage() {
  const { getToken } = useTenant();
  
  // State
  const [promoCodes, setPromoCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [redemptionHistory, setRedemptionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [toast, setToast] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    code: '',
    sms_quantity: '',
    max_redemptions: '',
    expires_at: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Fetch promo codes
  const fetchPromoCodes = async () => {
    setLoadingCodes(true);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/promo-codes?include_inactive=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch promo codes');
      
      const data = await response.json();
      setPromoCodes(data || []);
    } catch (err) {
      console.error('Error fetching promo codes:', err);
      showToast('error', 'Failed to load promo codes');
    } finally {
      setLoadingCodes(false);
    }
  };
  
  // Create promo code
  const handleCreatePromo = async (e) => {
    e.preventDefault();
    
    // Validate
    const errors = {};
    if (!form.code || form.code.length < 6) {
      errors.code = 'Code must be at least 6 characters';
    }
    if (!form.sms_quantity || form.sms_quantity <= 0) {
      errors.sms_quantity = 'Must be a positive number';
    }
    if (form.max_redemptions && form.max_redemptions <= 0) {
      errors.max_redemptions = 'Must be a positive number or leave empty for unlimited';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setSubmitting(true);
    setFormErrors({});
    
    try {
      const token = await getToken();
      
      // Build request body
      const body = {
        code: form.code.toUpperCase().trim(),
        sms_quantity: parseInt(form.sms_quantity),
        description: form.description || null
      };
      
      // Add optional fields
      if (form.max_redemptions) {
        body.max_redemptions = parseInt(form.max_redemptions);
      }
      
      if (form.expires_at) {
        // Convert to ISO format with timezone
        const date = new Date(form.expires_at);
        body.expires_at = date.toISOString();
      }
      
      const response = await fetch(`${BACKEND_URL}/api/admin/promo-codes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create promo code');
      }
      
      // Success
      await fetchPromoCodes();
      setShowCreateForm(false);
      setForm({ code: '', sms_quantity: '', max_redemptions: '', expires_at: '', description: '' });
      showToast('success', 'Promo code created successfully');
      
    } catch (err) {
      console.error('Error creating promo code:', err);
      showToast('error', err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Deactivate promo code
  const handleDeactivate = async (code) => {
    if (!window.confirm(`Are you sure you want to deactivate promo code "${code}"? This cannot be undone, but historical redemptions will be preserved.`)) {
      return;
    }
    
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/promo-codes/${code}/deactivate`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to deactivate promo code');
      }
      
      await fetchPromoCodes();
      showToast('success', `Promo code "${code}" has been deactivated`);
      
    } catch (err) {
      console.error('Error deactivating promo code:', err);
      showToast('error', err.message);
    }
  };
  
  // View redemption history
  const viewRedemptionHistory = async (code) => {
    setSelectedPromo(code);
    setLoadingHistory(true);
    
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/promo-codes/${code}/redemptions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch redemption history');
      
      const data = await response.json();
      setRedemptionHistory(data || []);
    } catch (err) {
      console.error('Error fetching redemption history:', err);
      showToast('error', 'Failed to load redemption history');
    } finally {
      setLoadingHistory(false);
    }
  };
  
  // Toast helper
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };
  
  // Initialize
  useEffect(() => {
    fetchPromoCodes();
  }, []);
  
  // Get status badge
  const getStatusBadge = (promo) => {
    if (!promo.is_active) {
      return <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">Inactive</span>;
    }
    if (promo.is_expired) {
      return <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">Expired</span>;
    }
    if (promo.max_redemptions && promo.current_redemptions >= promo.max_redemptions) {
      return <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-700">Max Reached</span>;
    }
    return <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">Active</span>;
  };
  
  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Promo Code Management</h1>
          <p className="text-gray-600 mt-1">
            Create and manage promotional SMS credit codes
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
        >
          <Plus className="w-4 h-4" />
          Create Promo Code
        </button>
      </div>
      
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600" />
          )}
          <span className={`text-sm font-medium ${
            toast.type === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {toast.message}
          </span>
        </div>
      )}
      
      {/* Create Promo Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4" onKeyDown={(e) => { if (e.key === 'Escape') { setShowCreateForm(false); setFormErrors({}); } }} onClick={(e) => { if (e.target === e.currentTarget) { setShowCreateForm(false); setFormErrors({}); } }}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Create Promo Code</h2>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setForm({ code: '', sms_quantity: '', max_redemptions: '', expires_at: '', description: '' });
                  setFormErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleCreatePromo} className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <strong>Important:</strong> Once created, promo codes cannot be edited. Only deactivation is allowed.
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Code * (6-20 characters)
                  </label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="WELCOME2026"
                    maxLength={20}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 uppercase ${
                      formErrors.code ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  {formErrors.code && (
                    <p className="text-sm text-red-600 mt-1">{formErrors.code}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Alphanumeric, dash, underscore allowed</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SMS Quantity *
                  </label>
                  <input
                    type="number"
                    value={form.sms_quantity}
                    onChange={(e) => setForm({ ...form, sms_quantity: parseInt(e.target.value) || '' })}
                    placeholder="500"
                    min="1"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                      formErrors.sms_quantity ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  {formErrors.sms_quantity && (
                    <p className="text-sm text-red-600 mt-1">{formErrors.sms_quantity}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Redemptions (optional)
                  </label>
                  <input
                    type="number"
                    value={form.max_redemptions}
                    onChange={(e) => setForm({ ...form, max_redemptions: parseInt(e.target.value) || '' })}
                    placeholder="Leave empty for unlimited"
                    min="1"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                      formErrors.max_redemptions ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  {formErrors.max_redemptions && (
                    <p className="text-sm text-red-600 mt-1">{formErrors.max_redemptions}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Leave empty for unlimited</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date (optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty for never expires</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Internal Description (optional)
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Internal note about this promo code..."
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">{form.description.length}/500</p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setForm({ code: '', sms_quantity: '', max_redemptions: '', expires_at: '', description: '' });
                    setFormErrors({});
                  }}
                  className="px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create Promo Code
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Promo Codes List */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Tag className="w-5 h-5" />
          Promo Codes
        </h2>
        
        {loadingCodes ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : promoCodes.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No promo codes created yet</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="mt-4 text-purple-600 hover:underline text-sm"
            >
              Create your first promo code
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Code</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">SMS</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Redemptions</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Expiry</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Created</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {promoCodes.map((promo) => (
                  <tr key={promo.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-mono font-bold text-gray-900">{promo.code}</div>
                      {promo.description && (
                        <div className="text-xs text-gray-500 mt-1">{promo.description}</div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-medium">
                      {promo.sms_quantity.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="font-medium">{promo.current_redemptions.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">
                        of {promo.max_redemptions ? promo.max_redemptions.toLocaleString() : '∞'}
                      </div>
                      {promo.redemptions_remaining !== null && (
                        <div className="text-xs text-purple-600 mt-1">
                          {promo.redemptions_remaining} left
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {getStatusBadge(promo)}
                    </td>
                    <td className="py-3 px-4">
                      {promo.expires_at ? (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {new Date(promo.expires_at).toLocaleDateString('en-AU', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Never</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(promo.created_at).toLocaleDateString('en-AU', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => viewRedemptionHistory(promo.code)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="View redemption history"
                        >
                          <History className="w-4 h-4" />
                        </button>
                        {promo.is_active && (
                          <button
                            onClick={() => handleDeactivate(promo.code)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Deactivate promo code"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Redemption History Modal */}
      {selectedPromo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4" onKeyDown={(e) => { if (e.key === 'Escape') { setSelectedPromo(null); setRedemptionHistory([]); } }} onClick={(e) => { if (e.target === e.currentTarget) { setSelectedPromo(null); setRedemptionHistory([]); } }}>
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Redemption History</h2>
                <p className="text-sm text-gray-600 mt-1">Promo code: <span className="font-mono font-bold">{selectedPromo}</span></p>
              </div>
              <button
                onClick={() => {
                  setSelectedPromo(null);
                  setRedemptionHistory([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                </div>
              ) : redemptionHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No redemptions yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Business</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Pack ID</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Redeemed Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {redemptionHistory.map((redemption) => (
                        <tr key={redemption.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="font-medium text-gray-900">{redemption.business_name}</div>
                            <div className="text-xs text-gray-500 font-mono">{redemption.business_id.slice(0, 8)}...</div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-mono text-xs text-gray-600">
                              {redemption.sms_pack_id.slice(0, 12)}...
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {new Date(redemption.redeemed_at).toLocaleString('en-AU', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-4 text-sm text-gray-600">
                    Total redemptions: {redemptionHistory.length}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
